import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Mail, Phone, MapPin, Clock, Send, ArrowRight, MessageSquare, 
  CheckCircle, AlertCircle, Users
} from 'lucide-react';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission (replace with actual Supabase integration)
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // For MVP, just log to console
      console.log('Contact form submitted:', formData);
      
      setSubmitStatus('success');
      setFormData({ name: '', email: '', message: '' });
      
      // Reset success message after 5 seconds
      setTimeout(() => setSubmitStatus('idle'), 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email",
      value: "hello@startupstreak.com",
      description: "We'll get back to you within 24 hours"
    },
    {
      icon: Clock,
      title: "Response Time",
      value: "24 hours",
      description: "We're committed to quick responses"
    },
    {
      icon: MessageSquare,
      title: "Support",
      value: "Live Chat",
      description: "Available during business hours"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-blue-600 via-teal-500 to-orange-400 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-white/10" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-light mb-8 leading-tight">
              Get in
              <br />
              <span className="font-bold">Touch</span>
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-12 leading-relaxed">
              Have questions about your streak? Need help getting started? 
              We're here to help you succeed.
            </p>
            <div className="flex items-center justify-center gap-4 text-blue-100">
              <CheckCircle className="w-5 h-5" />
              <span>24-hour response time</span>
              <span>•</span>
              <span>Free consultation</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-32 bg-white text-black">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 max-w-6xl mx-auto">
            {/* Contact Form */}
            <div>
              <div className="mb-12">
                <h2 className="text-4xl md:text-5xl font-light mb-6">Send us a Message</h2>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Tell us about your business idea or ask any questions. 
                  We'll help you get started on your 30-day streak.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email address"
                    className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your business idea or ask your question..."
                    rows={6}
                    className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent resize-none"
                  />
                </div>

                {submitStatus === 'success' && (
                  <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-green-800">Message sent successfully! We'll get back to you soon.</span>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <span className="text-red-800">Something went wrong. Please try again.</span>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-black text-white hover:bg-gray-800 text-lg py-4 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Sending Message...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      Send Message
                      <Send className="w-5 h-5" />
                    </div>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <div className="mb-12">
                <h2 className="text-4xl md:text-5xl font-light mb-6">Contact Information</h2>
                <p className="text-xl text-gray-600 leading-relaxed">
                  We're here to help you succeed. Reach out through any of these channels.
                </p>
              </div>

              <div className="space-y-8">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h3 className="text-xl font-medium text-black mb-2">{info.title}</h3>
                      <p className="text-lg text-gray-600 mb-1">{info.value}</p>
                      <p className="text-gray-500">{info.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* FAQ Link */}
              <div className="mt-12 p-8 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-medium text-black mb-4">Quick Questions?</h3>
                <p className="text-gray-600 mb-6">
                  Check out our frequently asked questions for quick answers to common queries.
                </p>
                <Link to="/pricing" className="inline-flex items-center gap-2 text-black font-medium hover:underline">
                  View FAQ
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Contact Us */}
      <section className="py-32 bg-gray-50 text-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-light mb-8">Why Contact Us?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We're not just another SaaS company. We're entrepreneurs helping entrepreneurs.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 rounded-xl bg-black flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-medium text-black mb-4">Personalized Guidance</h3>
              <p className="text-gray-600 leading-relaxed">
                Get specific advice tailored to your business idea and current situation.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-xl bg-black flex items-center justify-center mx-auto mb-6">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-medium text-black mb-4">Quick Response</h3>
              <p className="text-gray-600 leading-relaxed">
                We understand time is money. Get answers within 24 hours, guaranteed.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-xl bg-black flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-medium text-black mb-4">Community Access</h3>
              <p className="text-gray-600 leading-relaxed">
                Connect with fellow entrepreneurs and get support from our community.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-teal-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-white/5" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-light mb-8 leading-tight">
              Ready to Start Your Streak?
            </h2>
            <p className="text-xl text-blue-200 mb-12 leading-relaxed">
              Don't wait for the perfect moment. Start building your business today.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Link to="/signup">
                <Button size="lg" className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-300 hover:to-orange-400 text-black text-lg px-12 py-4 font-semibold shadow-2xl hover:shadow-yellow-400/25 transition-all">
                  Start Your Streak Now
                </Button>
              </Link>
              <div className="flex items-center gap-2 text-blue-200">
                <CheckCircle className="w-5 h-5" />
                <span>Free Plan</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
